import { useState } from 'react'
import { RotateCcw, Plus, Trash2, CheckCircle2, XCircle, Clock, Loader2, Info, Eye, ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { toast } from 'sonner'
import { useTemplates, useCreateTemplate, useDeleteTemplate } from '@/api/hooks/useBroadcast'

// CASCAS NEUTRAS — o que a Meta revisa/aprova como UTILITY (mesmas provadas no broadcast).
// REGRA: nada de "acessar/clique/aproveite" nem link cru — dispara rejeição por marketing.
// O conteúdo apelativo real vai no {{1}} (só no envio; a Meta não revisa isso).
const SHELLS = [
  'Atualização na sua conta. {{1}} Toque no botão abaixo para conferir.',
  'Há uma atualização na sua conta. {{1}} Toque no botão abaixo para conferir.',
  'Registramos uma atualização na sua conta. {{1}} Toque no botão abaixo para conferir.',
]

// Preenche {{1}} e {{first_name}} numa casca (pra preview)
function fill(shell: string, v1: string): string {
  return shell
    .replace(/\{\{\s*1\s*\}\}/g, v1)
    .replace(/\{\{\s*first_name\s*\}\}/g, 'Maria')
    .replace(/\s+/g, ' ')
    .trim()
}

function slug(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40)
}

export function LeadRecovery() {
  const { data: templates = [], isLoading } = useTemplates()
  const createTpl = useCreateTemplate()
  const deleteTpl = useDeleteTemplate()

  const recoveryTemplates = templates.filter((t) => t.isRecovery)
  const totalSent = recoveryTemplates.reduce((s, t) => s + (t.recoverySent || 0), 0)
  const totalClicks = recoveryTemplates.reduce((s, t) => s + (t.recoveryClicks || 0), 0)
  const ctr = totalSent > 0 ? ((totalClicks / totalSent) * 100).toFixed(1) : '0'

  const [name, setName] = useState('')
  // Conteúdo REAL (o lead recebe) — vai no {{1}}, a Meta não revisa
  const [var1, setVar1] = useState('')
  // Máscara (o que a Meta revisa)
  const [shell, setShell] = useState(SHELLS[0])
  const [example, setExample] = useState('detalhes')
  // Botão postback + resposta ao clicar
  const [btnText, setBtnText] = useState('Ver agora')
  const [replyText, setReplyText] = useState('Oii 😍 tá aqui ó, toque pra ver 👇')
  const [replyLink, setReplyLink] = useState('')
  const [replyBtnText, setReplyBtnText] = useState('Acessar')
  const [creating, setCreating] = useState(false)

  const uses1 = /\{\{\s*1\s*\}\}/.test(shell)
  const metaSees = fill(shell, example)        // o que a Meta revisa (neutro)
  const leadGets = fill(shell, var1 || '…')    // o que o lead recebe (real)

  async function handleCreate() {
    const finalName = slug(name) || `recup_${Date.now().toString(36)}`
    if (!shell.trim()) { toast.error('A máscara não pode ficar vazia.'); return }
    if (uses1 && !var1.trim()) { toast.error('Preencha o conteúdo real (o que o lead vê).'); return }
    if (!btnText.trim()) { toast.error('Defina o texto do botão.'); return }
    if (!replyLink.trim()) { toast.error('Defina o link que a pessoa recebe ao clicar.'); return }

    setCreating(true)
    try {
      const res = await createTpl.mutateAsync({
        name: finalName,
        body: shell,                       // CASCA neutra → o que a Meta aprova
        var1: uses1 ? var1.trim() : undefined,  // conteúdo real → só no envio
        example,                           // exemplo NEUTRO do {{1}} → o que a Meta vê
        isRecovery: true,
        // Botão POSTBACK: ao clicar, reabre a janela de 24h e entrega a resposta (reply_*).
        buttons: [{
          type: 'postback',
          text: btnText.trim(),
          payload: '',
          reply_text: replyText.trim(),
          reply_link: replyLink.trim(),
          reply_btn_text: replyBtnText.trim() || 'Acessar',
        }],
      })
      if (res.approved > 0) {
        toast.success(`Template "${finalName}" aprovado em ${res.approved} página(s)!`)
      } else {
        const reasons = res.reasons && Object.keys(res.reasons).length
          ? Object.entries(res.reasons).map(([k, v]) => `${k} (${v})`).join(', ')
          : 'sem detalhe'
        toast.warning(`0 aprovados (${res.failed} falhas). Motivo: ${reasons}`, { duration: 8000 })
      }
      setName(''); setVar1(''); setReplyLink('')
    } catch (e) {
      toast.error('Falha ao criar: ' + (e as Error).message)
    } finally {
      setCreating(false)
    }
  }

  async function handleDelete(tplName: string) {
    if (!confirm(`Remover o template de recuperação "${tplName}"?`)) return
    try {
      await deleteTpl.mutateAsync(tplName)
      toast.success('Template removido.')
    } catch (e) {
      toast.error('Falha ao remover: ' + (e as Error).message)
    }
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10 text-primary"><RotateCcw className="w-6 h-6" /></div>
        <div>
          <h1 className="text-xl font-bold">Recuperação de Lead</h1>
          <p className="text-sm text-muted-foreground">Templates que reabrem a conversa com quem saiu da janela de 24h.</p>
        </div>
      </div>

      {/* Métricas */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-border p-4">
          <p className="text-xs text-muted-foreground">Enviados</p>
          <p className="text-2xl font-bold">{totalSent.toLocaleString('pt-BR')}</p>
        </div>
        <div className="rounded-xl border border-border p-4">
          <p className="text-xs text-muted-foreground">Reativações (cliques)</p>
          <p className="text-2xl font-bold">{totalClicks.toLocaleString('pt-BR')}</p>
        </div>
        <div className="rounded-xl border border-primary/40 bg-primary/5 p-4">
          <p className="text-xs text-muted-foreground">CTR (reativação)</p>
          <p className="text-2xl font-bold text-primary">{ctr}%</p>
        </div>
      </div>

      <div className="flex gap-2 p-3 rounded-lg bg-muted/50 text-sm text-muted-foreground">
        <Info className="w-5 h-5 shrink-0 text-primary" />
        <p>
          Quando um lead passa <strong>24h sem interagir</strong>, só dá pra mandar template aprovado. A sequência <strong>sorteia
          um destes</strong> (crie vários com frases diferentes pra não parecer robô). São de <strong>postback</strong>: a pessoa clica →
          a janela reabre → recebe seu link e volta pro fluxo com as fotos.
        </p>
      </div>

      <div className="rounded-xl border border-border p-4 space-y-5">
        <h2 className="font-semibold flex items-center gap-2"><Plus className="w-4 h-4" /> Novo template de recuperação</h2>

        {/* PASSO 1 — conteúdo real */}
        <div className="space-y-1.5">
          <Label className="flex items-center gap-1.5"><span className="text-xs font-bold bg-primary text-primary-foreground rounded px-1.5 py-0.5">1</span> Conteúdo real — o que o lead recebe</Label>
          <Textarea value={var1} onChange={(e) => setVar1(e.target.value)} rows={2}
            placeholder="Ex: Oi {{first_name}} 😏 sumida! Preparei algo especial pra você hoje" />
          <p className="text-xs text-muted-foreground">Entra no <code className="bg-muted px-1 rounded">{'{{1}}'}</code> só no envio — a Meta NÃO revisa isso. Use <code className="bg-muted px-1 rounded">{'{{first_name}}'}</code> pro nome.</p>
        </div>

        {/* PASSO 2 — máscara */}
        <div className="space-y-1.5">
          <Label className="flex items-center gap-1.5"><span className="text-xs font-bold bg-amber-500 text-white rounded px-1.5 py-0.5">2</span> Máscara neutra — o que a Meta revisa <ShieldCheck className="w-3.5 h-3.5 text-amber-600" /></Label>
          <select value={shell} onChange={(e) => setShell(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
            {SHELLS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="flex items-center gap-2">
            <Label className="text-xs shrink-0">Exemplo neutro do {'{{1}}'}:</Label>
            <Input value={example} onChange={(e) => setExample(e.target.value)} className="h-8" placeholder="detalhes" />
          </div>
          <p className="text-xs text-muted-foreground">Mantenha neutro (ex: "detalhes", "novidades"). É o que a Meta vê na revisão — se parecer propaganda, rejeita.</p>
        </div>

        {/* Botão + resposta */}
        <div className="border-t border-border pt-4 space-y-4">
          <p className="text-sm font-medium">Botão e resposta ao clicar:</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Texto do botão</Label>
              <Input value={btnText} onChange={(e) => setBtnText(e.target.value)} placeholder="Ver agora" />
              <p className="text-xs text-muted-foreground">Sem emoji — a Meta rejeita emoji no texto do botão.</p></div>
            <div className="space-y-1.5"><Label>Nome (opcional)</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="auto: recup_..." /></div>
            <div className="space-y-1.5"><Label>Mensagem de resposta</Label>
              <Input value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Oii 😍 tá aqui ó 👇" /></div>
            <div className="space-y-1.5"><Label>Link (pro site)</Label>
              <Input value={replyLink} onChange={(e) => setReplyLink(e.target.value)} placeholder="https://t.co/..." /></div>
            <div className="space-y-1.5"><Label>Texto do botão da resposta</Label>
              <Input value={replyBtnText} onChange={(e) => setReplyBtnText(e.target.value)} placeholder="Acessar" /></div>
          </div>
        </div>

        {/* Preview cloak */}
        <div className="grid md:grid-cols-2 gap-3">
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
            <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-600 mb-1"><ShieldCheck className="w-3.5 h-3.5" /> O que a Meta revisa</p>
            <p className="text-muted-foreground">{metaSees}</p>
          </div>
          <div className="rounded-lg border border-primary/40 bg-primary/5 p-3 text-sm">
            <p className="flex items-center gap-1.5 text-xs font-semibold text-primary mb-1"><Eye className="w-3.5 h-3.5" /> O que o lead recebe</p>
            <p className="text-muted-foreground">{leadGets}</p>
          </div>
        </div>

        <Button onClick={handleCreate} disabled={creating} className="w-full md:w-auto">
          {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Criando e aprovando...</> : <>Criar e aprovar nas páginas</>}
        </Button>
      </div>

      {/* Lista */}
      <div className="space-y-3">
        <h2 className="font-semibold">Seus templates de recuperação ({recoveryTemplates.length})</h2>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Carregando...</p>
        ) : recoveryTemplates.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border rounded-lg">
            Nenhum template de recuperação ainda. Crie o primeiro acima.
          </p>
        ) : (
          recoveryTemplates.map((t) => (
            <div key={t.id} className="rounded-lg border border-border p-3 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="font-medium truncate">{t.name}</p>
                <p className="text-sm text-muted-foreground truncate">{t.var1 || t.body}</p>
                <div className="flex gap-3 mt-1 text-xs">
                  <span className="flex items-center gap-1 text-green-600"><CheckCircle2 className="w-3.5 h-3.5" /> {t.approved} aprov.</span>
                  {t.rejected > 0 && <span className="flex items-center gap-1 text-red-600"><XCircle className="w-3.5 h-3.5" /> {t.rejected} rej.</span>}
                  {t.pending > 0 && <span className="flex items-center gap-1 text-amber-600"><Clock className="w-3.5 h-3.5" /> {t.pending} pend.</span>}
                  <span className="text-muted-foreground">de {t.totalPages} páginas</span>
                </div>
                <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                  <span>{(t.recoverySent || 0).toLocaleString('pt-BR')} env.</span>
                  <span>{(t.recoveryClicks || 0).toLocaleString('pt-BR')} cliques</span>
                  <span className="font-medium text-primary">CTR {(t.recoverySent || 0) > 0 ? (((t.recoveryClicks || 0) / (t.recoverySent as number)) * 100).toFixed(1) : '0'}%</span>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => handleDelete(t.name)} className="text-red-500 shrink-0">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
